import { useCallback, useEffect, useRef, useState } from "react";
import { getCharacterImage, streamCharacterImage } from "../services/OpenAiService";

const memCache = new Map(); // cacheKey -> url

// Owns the full lifecycle of a streamed battle-scene image: the initial
// generation (gpt-image-2 streaming, or the non-streaming gpt-image-1.5
// fallback) and on-demand regeneration, sharing one partial/done/error
// handler builder between both instead of duplicating it per call site.
// This repo has no architecture doc of its own — for the full cross-repo
// picture (safety ownership, the streaming state machine, the presentation
// contract this hook consumes, caching/regeneration semantics on the
// backend), see the canonical reference: the sibling backend repo's
// who-would-win-server/docs/architecture/image-generation-safety.md.
//
// Attempt-identity protection (Phase E): every generation/regeneration
// attempt gets its own AbortController, and every async callback capable of
// mutating state (onPartial/onDone/onError, plus the non-streaming path's
// own success/catch branches) checks `controller.signal.aborted` before
// writing — a stale attempt superseded by a newer one can never mutate the
// current attempt's state, including a late "done" that would otherwise
// overwrite the current url with stale canonical media. A new attempt
// always aborts the prior controller before/while setting up its own, so a
// superseded attempt's own late settlement reliably reads itself as
// aborted.
export function useImageStream({ prompt, imageKey, size = "1024x1024", model = "gpt-image-2" }) {
  const [status, setStatus] = useState("idle");
  const [url, setUrl] = useState(null);
  const [partialSrc, setPartialSrc] = useState(null);
  const [partialIndex, setPartialIndex] = useState(0);
  const [errCode, setErrCode] = useState("");
  const [errPolicy, setErrPolicy] = useState(null);
  const [presentationKey, setPresentationKey] = useState(null);
  const [previewEligible, setPreviewEligible] = useState(false);
  const [categoryDetails, setCategoryDetails] = useState([]);
  const [regenerating, setRegenerating] = useState(false);
  const [regenErrCode, setRegenErrCode] = useState("");
  const [regenErrPolicy, setRegenErrPolicy] = useState(null);
  const [regenPresentationKey, setRegenPresentationKey] = useState(null);
  const [regenPreviewEligible, setRegenPreviewEligible] = useState(false);
  const [regenCategoryDetails, setRegenCategoryDetails] = useState([]);
  // Bumped to force the initial-generation effect to re-run after the user
  // accepts a required policy update — see makeStreamHandlers' onError.
  const [retryTick, setRetryTick] = useState(0);
  const abortRef = useRef(null);
  const regenAbortRef = useRef(null);
  const regenerateRef = useRef(null); // always the latest regenerate(), see its own comment
  // Mirrors `url` synchronously for handlers whose closures were memoized
  // before the latest render (see makeStreamHandlers' useCallback deps) —
  // regen's failure handler needs to know "does a canonical image exist
  // right now" without depending on (and re-creating handlers whenever)
  // `url` itself changes.
  const urlRef = useRef(null);

  const cacheKey = `${imageKey}::${size}`;

  const setCanonicalUrl = useCallback((next) => {
    urlRef.current = next;
    setUrl(next);
  }, []);

  const clearAttemptPresentation = useCallback(() => {
    setErrCode("");
    setErrPolicy(null);
    setPresentationKey(null);
    setPreviewEligible(false);
    setCategoryDetails([]);
  }, []);

  const clearRegenPresentation = useCallback(() => {
    setRegenErrCode("");
    setRegenErrPolicy(null);
    setRegenPresentationKey(null);
    setRegenPreviewEligible(false);
    setRegenCategoryDetails([]);
  }, []);

  const makeStreamHandlers = useCallback((controller, { onSuccess, onFailure, onBlocked, onRetry }) => ({
    onPartial: ({ b64, index }) => {
      if (controller.signal.aborted) return; // stale — a newer attempt has superseded this one
      setPartialSrc(`data:image/png;base64,${b64}`);
      setPartialIndex(index ?? 0);
    },
    onDone: ({ url: finalUrl }) => {
      if (controller.signal.aborted) return; // stale — a newer attempt has superseded this one
      memCache.set(cacheKey, finalUrl);
      setCanonicalUrl(finalUrl);
      setPartialSrc(null);
      setPartialIndex(0);
      setStatus("ready");
      onSuccess();
    },
    onError: ({ errorCode, policy, requiredPolicies, presentationKey: key, previewEligible: preview, categoryDetails: categories }) => {
      if (controller.signal.aborted) return; // stale — a newer attempt has superseded this one
      // A generic-policy gate, not a generation failure — divert to the
      // update modal and retry once accepted, same as auth_required_for_battle
      // diverts to the sign-in modal in useBattleRequest.js. Never surfaced
      // as errCode/presentationKey, so BattleImage.js never renders a broken
      // "couldn't be generated" state behind the modal. onBlocked resets any
      // "in progress" flag immediately (not deferred to onRetry) so the UI
      // isn't stuck mid-action for however long the modal stays open.
      if (errorCode === "policy_acceptance_required") {
        setPartialSrc(null);
        setPartialIndex(0);
        onBlocked?.();
        window.dispatchEvent(new CustomEvent("policy:update-required", {
          detail: { requiredPolicies, onAccepted: onRetry },
        }));
        return;
      }
      onFailure(
        errorCode || "generation_failed",
        policy ?? null,
        key ?? null,
        Boolean(preview),
        Array.isArray(categories) ? categories : []
      );
    },
  }), [cacheKey, setCanonicalUrl]);

  useEffect(() => {
    if (!prompt?.trim() || !imageKey?.trim()) return;

    abortRef.current?.abort?.();
    const controller = new AbortController();
    abortRef.current = controller;

    const hit = memCache.get(cacheKey);
    if (hit) {
      setCanonicalUrl(hit);
      setStatus("ready");
      setPartialSrc(null);
      setPartialIndex(0);
      clearAttemptPresentation();
      return;
    }

    setStatus("loading");
    setCanonicalUrl(null);
    setPartialSrc(null);
    setPartialIndex(0);
    clearAttemptPresentation();

    if (model === "gpt-image-2") {
      streamCharacterImage({
        prompt, imageKey, size,
        signal: controller.signal,
        ...makeStreamHandlers(controller, {
          onSuccess: () => {},
          onFailure: (code, policy, key, preview, categories) => {
            setErrCode(code);
            setErrPolicy(policy);
            setPresentationKey(key);
            setPreviewEligible(preview);
            setCategoryDetails(categories);
            if (!preview) {
              setPartialSrc(null);
              setPartialIndex(0);
            }
            setStatus("error");
          },
          onRetry: () => setRetryTick((t) => t + 1),
        }),
      });
    } else {
      // gpt-image-1.5 path — non-streaming (future free tier)
      (async () => {
        try {
          const data = await getCharacterImage({ prompt, imageKey, size, signal: controller.signal });
          if (controller.signal.aborted) return; // stale — a newer attempt has superseded this one
          memCache.set(cacheKey, data.url);
          setCanonicalUrl(data.url);
          setStatus("ready");
        } catch (e) {
          if (e?.name === "AbortError" || controller.signal.aborted) return;
          if (e?.errorCode === "policy_acceptance_required") {
            window.dispatchEvent(new CustomEvent("policy:update-required", {
              detail: { requiredPolicies: e.requiredPolicies, onAccepted: () => setRetryTick((t) => t + 1) },
            }));
            return;
          }
          setErrCode(e?.errorCode || "generation_failed");
          setErrPolicy(e?.policy ?? null);
          setPresentationKey(e?.presentationKey ?? null);
          setPreviewEligible(Boolean(e?.previewEligible));
          setCategoryDetails(Array.isArray(e?.categoryDetails) ? e.categoryDetails : []);
          setStatus("error");
        }
      })();
    }

    return () => controller.abort();
  }, [prompt, imageKey, size, cacheKey, model, makeStreamHandlers, retryTick, setCanonicalUrl, clearAttemptPresentation]);

  // Abort an in-flight regenerate if the component unmounts mid-request.
  useEffect(() => {
    return () => regenAbortRef.current?.abort?.();
  }, []);

  const regenerate = useCallback(async () => {
    if (!(status === "ready" || status === "error") || regenerating) return;
    regenAbortRef.current?.abort?.();
    setRegenerating(true);
    clearRegenPresentation();
    setPartialSrc(null);
    setPartialIndex(0);
    // A fresh attempt must never display stale copy from an earlier failed
    // regeneration that had no canonical image to fall back to (and so had
    // promoted itself into the top-level error state below) — clear it too.
    if (!urlRef.current) clearAttemptPresentation();

    const controller = new AbortController();
    regenAbortRef.current = controller;

    await streamCharacterImage({
      prompt, imageKey, size,
      endpoint: "/api/battle-image/regenerate/stream",
      signal: controller.signal,
      ...makeStreamHandlers(controller, {
        onSuccess: () => setRegenerating(false),
        onFailure: (code, policy, key, preview, categories) => {
          const hadCanonical = Boolean(urlRef.current);
          setRegenErrCode(code);
          setRegenErrPolicy(policy);
          setRegenPresentationKey(key);
          setRegenPreviewEligible(preview);
          setRegenCategoryDetails(categories);
          setRegenerating(false);

          if (hadCanonical) {
            // Canonical wins — never show this attempt's incomplete partial
            // once a completed image already exists; the regen-row notice
            // (built from the regen* fields above) carries the failure
            // reason instead, it is never fully suppressed.
            setPartialSrc(null);
            setPartialIndex(0);
            return;
          }

          // No canonical to fall back to (a blocked baseline, or a fresh
          // attempt that never completed before) — this failure becomes the
          // CURRENT top-level state so the primary error block reflects the
          // latest attempt, not a stale earlier one. previewEligible's
          // normal rule applies since there's nothing to restore.
          setErrCode(code);
          setErrPolicy(policy);
          setPresentationKey(key);
          setPreviewEligible(preview);
          setCategoryDetails(categories);
          setStatus("error");
          if (!preview) {
            setPartialSrc(null);
            setPartialIndex(0);
          }
        },
        // Reset immediately (not deferred to onRetry) so the button isn't
        // stuck showing "Regenerating…" for however long the modal stays
        // open. Retry itself is deferred to the next tick via the ref
        // (always the latest closure) as extra insurance against a stale
        // `regenerating` read, though by the time a user actually accepts,
        // React has long since flushed the reset above.
        onBlocked: () => setRegenerating(false),
        onRetry: () => setTimeout(() => regenerateRef.current?.(), 0),
      }),
    });
  }, [status, regenerating, prompt, imageKey, size, makeStreamHandlers, clearRegenPresentation, clearAttemptPresentation]);

  regenerateRef.current = regenerate;

  return {
    status, url, partialSrc, partialIndex,
    errCode, errPolicy, presentationKey, previewEligible, categoryDetails,
    regenerating,
    regenErrCode, regenErrPolicy, regenPresentationKey, regenPreviewEligible, regenCategoryDetails,
    regenerate,
  };
}
